import { memo, useCallback } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PlusCircle } from 'lucide-react';
import { ContactCardData } from '../plugins/with-contact-cards';

interface ContactCardListProps {
  contacts: ContactCardData[];
  onSelect: (contact: ContactCardData) => void;
  onCreate: () => void;
  className?: string;
}

const ContactCardItem = memo(
  ({
    contact,
    onSelect
  }: {
    contact: ContactCardData;
    onSelect: (contact: ContactCardData) => void;
  }) => (
    <CommandItem
      value={contact.id}
      onSelect={() => {
        onSelect(contact);
      }}
      className="flex items-center cursor-pointer"
    >
      <Avatar className="mr-2 h-6 w-6">
        {contact.image ? (
          <AvatarImage src={contact.image} alt={contact.name} />
        ) : (
          <AvatarFallback>{contact.name[0].toUpperCase()}</AvatarFallback>
        )}
      </Avatar>
      <div className="flex flex-col">
        <span className="font-medium">{contact.name}</span>
        {contact.phoneNumbers[0] && (
          <span className="text-xs text-muted-foreground">{contact.phoneNumbers[0].number}</span>
        )}
      </div>
    </CommandItem>
  )
);

ContactCardItem.displayName = 'ContactCardItem';

/**
 * Displays a searchable list of contact cards with a create option.
 * Uses ShadcnUI's Command component for consistent styling and behavior.
 */
export const ContactCardList = memo(
  ({ contacts, onSelect, onCreate, className }: ContactCardListProps) => {
    const handleCreate = useCallback(() => {
      onCreate();
    }, [onCreate]);

    return (
      <Command className={className}>
        <CommandInput placeholder="Search contacts..." />
        <CommandList>
          <CommandEmpty>No contacts found.</CommandEmpty>

          <CommandGroup heading="Create New">
            <CommandItem onSelect={handleCreate} className="cursor-pointer">
              <PlusCircle className="mr-2 h-4 w-4" />
              Create new contact
            </CommandItem>
          </CommandGroup>

          {contacts.length > 0 && (
            <CommandGroup heading="Saved Contacts">
              {contacts.map((contact) => (
                <ContactCardItem key={contact.id} contact={contact} onSelect={onSelect} />
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    );
  }
);

ContactCardList.displayName = 'ContactCardList';
